import { parseLocation } from './slug'

// The provider page's "← Results" button normally just pops history
// (navigate(-1)). But a visitor can land on the page directly — a shared link, a
// search-engine result, a new tab opened from a results card — with no in-app
// history to pop. In that case we try to reconstruct a results destination from
// the referrer so the button still goes somewhere useful, falling back to home
// otherwise (see routes/provider.tsx).
//
// Returns the results pathname+search to link to, or null when the referrer
// isn't one of our own /:country/:location/:specialty search pages. `origin` is
// the current page's origin (window.location.origin), so cross-origin
// referrers (a search engine, another site) are rejected — only a validated
// same-origin results URL earns the "← Results" affordance. The location
// segment uses the same parser as the results loader, keeping city, ZIP, and
// intl forms in sync.
export const resultsPathFromReferrer = (referrer: string, origin: string): string | null => {
  let url: URL
  try {
    url = new URL(referrer)
  } catch {
    // Empty string (no referrer) or a malformed value.
    return null
  }
  if (url.origin !== origin) {
    return null
  }
  // Results route is /:country/:location/:specialty — exactly four raw
  // pathname segments (leading slash, country, location, specialty) with no
  // empty segment sneaking through. Only "us" is routed today (routes.ts), but
  // the country segment isn't validated against an allowlist anywhere else in
  // the app either — it's matched by shape (2-letter lowercase, same as the
  // "intl-<city>--<country>" location slugs in slug.ts) so this keeps working
  // as more countries come online without another edit here.
  const segments = url.pathname.split('/')
  if (segments.length !== 4 || segments[0] !== '' || !/^[a-z]{2}$/.test(segments[1] ?? '')) {
    return null
  }
  const locationSlug = segments[2]
  const specialtySlug = segments[3]
  if (
    locationSlug == null ||
    locationSlug === '' ||
    specialtySlug == null ||
    specialtySlug === '' ||
    parseLocation(locationSlug) == null
  ) {
    return null
  }
  return url.pathname + url.search
}
